import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, TrendingUp, Users, Home, Award, Target, CheckCircle2 } from 'lucide-react';

const IncomeOpportunityPage = () => {
  const incomeStreams = [
    {
      title: "Personal Production",
      icon: DollarSign,
      description: "Earn commissions on every policy you personally help place",
      potential: "Unlimited based on your effort"
    },
    {
      title: "Renewals",
      icon: TrendingUp,
      description: "Build passive income from policies you've already placed",
      potential: "Grows each year you stay active"
    },
    {
      title: "Team Overrides",
      icon: Users,
      description: "Earn overrides on production from agents you've trained",
      potential: "Multiplied by team size"
    },
    {
      title: "Bonuses & Incentives",
      icon: Award,
      description: "Qualify for carrier bonuses and company incentives",
      potential: "Based on performance"
    }
  ];

  const timeline = [
    { period: "Year 1", focus: "Learning & First Clients", income: "Supplemental income while learning" },
    { period: "Year 2-3", focus: "Building Momentum", income: "Many advisors match or exceed prior income" },
    { period: "Year 4-5", focus: "Leadership Development", income: "Team building creates leverage" },
    { period: "Year 5+", focus: "Passive Income Growth", income: "Renewals + overrides create stability" }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="income-opportunity-page">
      <section className="py-24 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=1920)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-transparent to-[#020617]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Link to="/for-advisors" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
              ← Back to For Advisors
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Income Opportunity
            </h1>
            <p className="text-slate-300 text-xl mb-8">
              Build a career with unlimited income potential, passive income streams, and the flexibility to work on your terms.
            </p>
            <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 p-6 rounded-lg">
              <p className="text-slate-300">
                <span className="text-[#C5A059] font-semibold">Important:</span> Income results vary based on individual effort, market conditions, and other factors. There is no guarantee of income. Success requires dedication, ongoing learning, and consistent effort.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl text-white mb-12 text-center">Multiple Income Streams</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {incomeStreams.map((stream, idx) => {
              const Icon = stream.icon;
              return (
                <div key={idx} className="bg-slate-900/50 border border-slate-800 p-6 hover:border-[#C5A059]/30 transition-colors">
                  <div className="w-12 h-12 rounded-lg bg-[#C5A059]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{stream.title}</h3>
                  <p className="text-slate-400 text-sm mb-3">{stream.description}</p>
                  <p className="text-[#C5A059] text-xs font-semibold">{stream.potential}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl text-white mb-12 text-center">Typical Growth Timeline</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {timeline.map((phase, idx) => (
              <div key={idx} className="relative">
                <div className="text-5xl font-serif text-[#C5A059]/20 mb-2">{phase.period}</div>
                <h3 className="text-white font-semibold mb-2">{phase.focus}</h3>
                <p className="text-slate-400 text-sm">{phase.income}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">Why This Opportunity?</h2>
              <ul className="space-y-4">
                {[
                  "No income ceiling — earn based on your results",
                  "Build equity in your business over time",
                  "Passive income from renewals and overrides",
                  "Work from anywhere with flexible hours",
                  "Help families while building wealth for yours",
                  "Leadership path for those who want to build teams"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059] mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 p-8">
              <Target className="w-12 h-12 text-[#C5A059] mb-6" />
              <h3 className="text-white font-semibold text-xl mb-4">Is This Right For You?</h3>
              <p className="text-slate-400 text-sm mb-6">
                This opportunity is ideal for self-motivated individuals who are willing to learn, work hard, and build relationships. Success comes from consistent effort over time.
              </p>
              <Link
                to="/become-an-agent"
                className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-6 py-3 uppercase tracking-wider text-sm transition-all w-full"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IncomeOpportunityPage;
